import { SECTION_NAMES } from '@repo/common';
import type ISectionSettings from '../../../types/models/ISectionSettings';
import type IGameSettings from '../../../types/models/IGameSettings';
import type { ApiGameSettings } from 'types/api/api.types';
import { parseGameSettings } from 'shared/utils/gameParsingUtils';
import { gridBlocksCount } from './topGamesService';
import type { ITrendingGamesResponse } from 'core/api/endpoints/trending.api';


const createTrendingSection = (
  trendingSection: ISectionSettings,
  supportedGames: { [gameKey: string]: ApiGameSettings },
  trendingGames: ITrendingGamesResponse
): ISectionSettings => {
  const games: IGameSettings[] = [...trendingGames.games]
    // Games arrive already sorted by trendRatio from the API.
    .filter((entry) => Boolean(supportedGames[entry.gameKey]))
    .map((entry, idx) =>
      parseGameSettings({
        mutatedGame: { gameKey: entry.gameKey, order: idx + 1, imageLobby: 'vertical', imageSection: 'vertical' },
        gameData: supportedGames[entry.gameKey],
      })
    )
    .filter((game): game is IGameSettings => game !== null);


  // We don't have a topLimit in the section settings,
  // this is a just in case check if they add it in the future,
  // we will limit the games to the topLimit if it exists
  const limitedGames =
    trendingSection.topLimit && games.length >= trendingSection.topLimit
      ? games.slice(0, trendingSection.topLimit)
      : games;

  return { ...trendingSection, games: limitedGames, gridBlocks: gridBlocksCount(limitedGames) };
};


export const applyTrendingSection = (
  sections: ISectionSettings[],
  supportedGames: { [gameKey: string]: ApiGameSettings },
  trendingGames: ITrendingGamesResponse | undefined
): ISectionSettings[] => {
  const index = sections.findIndex((s) => s.name === SECTION_NAMES.TRENDING_GAMES);

  // No TRENDING_GAMES section configured — nothing to do.
  if (index === -1) {
    return sections;
  }

  const withoutTrending = () => sections.filter((s) => s.name !== SECTION_NAMES.TRENDING_GAMES);

  try {
    const built = trendingGames ? createTrendingSection(sections[index], supportedGames, trendingGames) : null;

    if (!built) {
      return withoutTrending();
    }

    const next = [...sections];
    next[index] = built;
    return next;
  } catch (error) {
    console.warn('[TrendingService] Failed to apply trending section:', error);
    return withoutTrending();
  }
};
